<script setup lang="ts">
import { ref, computed, nextTick, onMounted, watch, isReadonly } from "vue";
import {
  Currency,
  ItemBatch,
  ItemPackingPrice,
  ItemSerial,
  TaxDefinition,
  VariantList,
  Vendor,
  WarehouseShelf,
} from "../../../types/models"; // Add this line to import ItemBatch
import { api } from "../../../api/Api";
import { Item } from "../../../types/models";
import { formatDate, formatNumber, isEmpty } from "../../../utils/CommonFunctions";
import ItemGridSelectItems from "./ItemGridSelectItems.vue";
import { useScreenSize } from "../../../utils/useScreenSize";
import ItemSelectGrid from "./ItemSelectGrid.vue";
import ItemSearchMobile from "./ItemSearchMobile.vue";
import IconComponent from "../common/IconComponent.vue";
import TextInput from "../form/TextInput.vue";
import DateInput from "../form/DateInput.vue";
import SearchSelect from "../form/SearchSelect.vue";
import SelectDropDown from "../form/SelectDropDown.vue";
import TabComponent from "../form/TabComponent.vue";
import Buttons from "../form/Buttons.vue";
import SelectVariants from "./SelectVariants.vue";
import MessageBox from "../../../services/MessageBox";
import ItemsForm from "../../../features/inventory/itemmanagement/items/ItemsForm.vue";

//new codings

const props = defineProps<{
  items?: Item[] | [];
  readonly: boolean;
  warehouses_id?: number | string | null | undefined;
  price_lists_id?: number | string;
  currency?: Currency
  exchange_rate: number;
  allowNewRow: boolean;
  allowChangePrice?: boolean;
  allowDiscount?: boolean;
  ignoreBatchSerial?: boolean;
  shelfManaged?: boolean;
  shelfs?: WarehouseShelf[] | [];
  exchangeRate?: number;
  vendor?: Vendor | "";
}>();

const emit = defineEmits<{
  (e: "save"): void;
  (e: "items", items: Item[]): void;
}>();

const itemList = ref<Item[]>([]);
const { isMobile } = useScreenSize();

const shelfs = ref<WarehouseShelf[]>([])

function addItemRow(index: number) {
  if (props.readonly || !props.allowNewRow) return;
  if (index < 0) {
    index = itemList.value.length - 1;
  }

  if (
    itemList.value.length > 0 &&
    isEmpty(itemList.value[itemList.value.length - 1].name)
  ) {
    focusNewRow();
    return;
  }

  const newItem: Item = {
    id: itemList.value.length,
    items_id: "",
    photo: "",
    code: "",
    name: "",
    item_name: "",
    sales_name: "",
    item_types_id: "",
    tracked_by: "Quantity",
    flexi_price: false,
    ignore_discount: false,
    purchase_uom: "",
    purchase_uom_qty: 1,
    sales_uom: "",
    sales_uom_qty: 1,
    default_sales_uom_id: "",
    default_sales_uom_qty: 1,
    is_package: false,
    use_package_price: false,
    is_expiry: false,
    tax_details_id: "",
    inventory_uom_id: "",
    inventory_uom_qty: 1,
    purchase_uom_id: "",
    sales_uom_id: "",
    has_variants: false,
    variants: "",
    tax: "",
    tax_rate: 0,
    purchase_order_items_id: "",
    purchase_goods_receipts_id: "",
    warehouses_id: props.warehouses_id ?? null,
    item_variants_id: "",
    unit_of_measures_id: "",
    unit_of_measure: "",
    on_hand: 0,
    quantity: 1,
    uom_qty: 1,
    inventory_qty: 1,
    cost: 0,
    price: 0,
    selling_price: 0,
    original_price: 0,
    sub_total: 0,
    discount_p: 0,
    discount_a: 0,
    value_before_tax: 0,
    tax_amount: 0,
    net_total: 0,
    remarks: "",
    uid: "",
    batches: [],
    serials: [],
    packing_prices: [],
    quantity_prices: [],
    shelfs: [],
    variant_list: [],
  };
  shelfs.value = [];
  props.shelfs?.forEach((shelf) => {
    const s: WarehouseShelf = {
      id: shelf.id,
      sales_invoice_items_id: "",
      warehouses_id: props.warehouses_id ?? null,
      warehouse_shelfs_id: shelf.id,
      items_id: "",
      item_variants_id: "",
      quantity: 0,
      code: shelf.code,
      name: shelf.name,
      status_id: "",
      warehouse_shelf_code: null,
      warehouse_shelf_name: null,
      on_hand: null
    };
    shelfs.value.push(s);
  });

  itemList.value.push(newItem);
  focusNewRow();
}

const focusNewRow = () => {
  nextTick(() => {
    const index = itemList.value.length - 1;
    const input = document.getElementById("code_" + index);
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
};

// Computed Properties
const subtotal = computed(() => {
  return itemList.value.reduce((sum: number, item: Item) => {
    return sum + (item.value_before_tax ?? 0);
  }, 0);
});

const taxAmount = computed(() => {
  return itemList.value.reduce((sum: number, item: Item) => {
    return sum + (item.tax_amount ?? 0);
  }, 0);
});

const total = computed(() => {
  return subtotal.value + taxAmount.value;
});

const showItemsDialog = ref(false);

const addBatchRow = (item: Item) => {
  if (
    (item.batches && item.batches.length == 0) ||
    (item.batches && item.batches[item.batches.length - 1]?.quantity > 0)
  ) {
    const batch: ItemBatch = {
      items_id: item.items_id,
      warehouses_id: item.warehouses_id ?? undefined,
      batch_no: "",
      expiry_date: "",
      on_hand: item.on_hand,
      quantity: item.quantity - getBatchSum(item),
      shelfs: shelfs.value,
      uid: "",
      active: true,
    };
    item.batches?.push(batch);
  }
};

const taxDetails = ref<TaxDefinition[]>([
  { id: "", name: "", percentage: 0 } as TaxDefinition,
]);

const handleTax = (item: Item) => {
  const tax: TaxDefinition = taxDetails.value.find(
    (e) => e.id == item.tax_details_id
  ) || { id: "", name: "", percentage: 0 };
  item.tax_rate = tax ? tax.percentage : 0;
  updateLineTotal(item);
};

const addSerialRow = (item: Item) => {
  const serial: ItemSerial = {
    items_id: item.items_id,
    warehouses_id: item.warehouses_id ?? undefined,
    serial_no: "",
    expiry_date: "",
    on_hand: item.on_hand,
    quantity: 1,
    uid: "",
    active: true,
    shelfs: shelfs.value
  };
  item.serials?.push(serial);
};

function updateLineTotal(item: Item) {
  item.inventory_qty = (item.quantity || 1) * (item.purchase_uom_qty || 1);
  item.sub_total = (item.quantity || 1) * (item.cost || 0);
  item.discount_a = item.sub_total * (item.discount_p || 0 / 100);
  item.value_before_tax = item.sub_total - item.discount_a;
  item.tax_amount =
    ((item.sub_total - item.discount_a) * (item.tax_rate || 0)) / 100;
  item.net_total = item.sub_total - item.discount_a + item.tax_amount;

  emit("items", itemList.value);
}


const showVariantsDialog = ref(false);
const showItemForm = ref(false);
const selectedItemIndex = ref(-1);
const selectItems = ref<Item[] | []>([]);
const selectedItem = ref<Item>();
async function handleItemCodeEnter(item: Item, index: number) {
  selectedItemIndex.value = index;
  selectedItem.value = item;
  if (isEmpty(item.code)) return;
  const params = {
    search_query: item.code,
    price_lists_id: props.price_lists_id,
    warehouses_id: props.warehouses_id,
  };
  const response = await api.post(
    "inventory/item-management/items/search",
    params
  );
  if (response.success == true) {
    if (response.data.length == 1) {
      if (response.data[0].has_variants && !response.data[0].variant) {
        selectedItem.value = response.data[0];
        showVariantsDialog.value = true;
      } else {
        setItem(response.data[0]);
      }
    } else if (response.data.length > 1) {
      selectItems.value = response.data;
      showItemsDialog.value = true;
    } else {
      itemNotFound();
    }
  } else {
    itemNotFound();
  }
}

const handleSelectVariant = (item: Item) => {
  if (item && selectedItem.value) {
    selectedItem.value.code = item.code;
    handleItemCodeEnter(selectedItem.value, selectedItemIndex.value);
  }
};

const handleItemCreated = (item: any) => {
  if (item && selectedItem.value) {
    selectedItem.value.code = item.code;
    handleItemCodeEnter(selectedItem.value, selectedItemIndex.value);
  }
  showItemForm.value = false
}

const itemNotFound = () => {
  MessageBox.confirm({
      title: "Item Not Found",
      message: "The Item could not be found. Do you want to create a new item or a new Variant?",
      confirmText: "Create New Item",
      cancelText: "Close",
      extraButton: {
        text: "New Variant",
        action: () => {
          showVariantsDialog.value = true;
        },
      },
    }).then((confirmed) => {
      if (confirmed) {
        showItemForm.value = true;
      }
    });
}


const setItem = (result: any, copy: boolean = false) => {
  const item = selectedItem.value;
  if (item) {
    item.items_id = result.id;
    item.photo = result.photo;
    item.code = result.code;
    item.name = result.name;
    item.item = result.item;
    item.sales_name = result.sales_name;
    item.item_types_id = result.item_types_id;
    item.purchase_uom = result.purchase_uom;
    item.purchase_uom_qty = result.purchase_uom_qty;
    item.sales_uom = result.sales_uom;
    item.sales_uom_qty = result.sales_uom_qty;
    item.is_package = result.is_package;
    item.use_package_price = result.use_package_price;
    item.is_expiry = result.is_expiry;
    item.tax_details_id = result.tax_details_id;
    item.inventory_uom_id = result.inventory_uom_id;
    item.purchase_uom_id = result.purchase_uom_id;
    item.sales_uom_id = result.sales_uom_id;
    item.has_variants = result.has_variants;
    item.variants = result.variants;
    item.tax = result.tax;
    item.tax_rate = result.tax_rate;
    item.uid = result.uid;
    item.tracked_by = result.tracked_by;
    item.default_sales_uom_id = result.purchase_uom_id;
    item.default_sales_uom_qty = result.purchase_uom_qty;
    //item.purchase_order_items_id = result.purchase_order_items_id;
    //item.purchase_goods_receipts_id = result.purchase_goods_receipts_id;
    //item.warehouses_id = result.warehouses_id;
    item.item_variants_id = result.item_variants_id;
    item.unit_of_measures_id = result.unit_of_measures_id;
    item.unit_of_measure = result.unit_of_measure;
    item.on_hand = result.on_hand;
    item.quantity = result.quantity ?? 1;
    item.uom_qty = result.purchase_uom_qty;
    item.inventory_qty = 1;
    item.cost = (result.cost * result.purchase_uom_qty) / (props.exchangeRate || 1);
    item.price = result.selling_price;
    item.sub_total = 0;
    item.discount_p = 0;
    item.discount_a = 0;
    item.value_before_tax = 0;
    item.tax_amount = result.tax_rate;
    item.remarks = "";
    item.remarks = "";
    if (props.readonly && !copy) {
      item.batches = result.batches;
      item.serials = result.serials;
    }
    item.packing_prices = result.packing_prices;
    item.quantity_prices = result.quantity_prices;
    item.variant_list = result.variant_list

    updateLineTotal(item);

    if (
      (item.tracked_by == "Batch" ||
        item.tracked_by == "Serial" ||
        props.shelfManaged) &&
      !props.ignoreBatchSerial
    ) {
      expandedRows.value.add(String(item.id));
    }

    if (item.tracked_by == "Batch" && !props.ignoreBatchSerial) {
      activeTab.value = "batch";
      addBatchRow(item);      
    } else if (item.tracked_by == "Serial" && !props.ignoreBatchSerial) {
      activeTab.value = "Serial";
      addSerialRow(item);      
    } else if (props.shelfManaged && !props.ignoreBatchSerial) {
      activeTab.value = "shelfs";
      console.log(shelfs.value)
      item.shelfs = shelfs.value
    }

    showItemsDialog.value = false;
    // Focus the quantity input
    nextTick(() => {
      const input = document.getElementById(
        "quantity_" + selectedItemIndex.value
      );
      if (input) {
        input.focus();
        (input as HTMLInputElement).select();
      }
    });
  }
};

async function getTaxList() {
  const response = await api.post("finance/master-data/tax-definitions/list");
  if (response.success == true) {
    taxDetails.value = response.data;
  }
}

function handleQuantityEnter(index: number, mobile: boolean = false) {
  nextTick(() => {
    const input = document.getElementById("cost_" + (mobile ? 'mobile_' : '') + index);
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
}

function handleCostEnter(index: number, mobile: boolean = false) {
  nextTick(() => {
    const input = document.getElementById("price_" + (mobile ? 'mobile_' : '') + index);
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
}

//TODO: handle batch for mobile 
function handleUnitPriceEnter(item: Item, index: number, mobile: boolean = false) {
  if ((item.batches?.length ?? 0) == 1 && !props.ignoreBatchSerial) {
    if (item.tracked_by == "Batch" && item.batches && item.batches.length > 0) {
      item.batches[0].quantity = item.inventory_qty ?? 0;
    }
  }
  if (item.tracked_by == "Serial" && !props.ignoreBatchSerial) {
    const serials: number = item.serials?.length ?? 0;
    if (serials < (item.inventory_qty ?? 0)) {
      for (let i = serials; i < (item.inventory_qty ?? 0); i++) {
        addSerialRow(item);
      }
    }
  }
  let input: HTMLElement | null = null;
  nextTick(() => {
    /*if (props.allowChangePrice) {
      input = document.getElementById("price_" + index);
  } else */
   if (mobile) addItemRow
    if (!props.ignoreBatchSerial) {
      if (
        itemList.value[index].tracked_by == "Batch" &&
        (item.batches?.length ?? 0) >= 1
      ) {
        activeTab.value = "batch";
        input = document.getElementById(
          "batch_code_" + item.items_id + "_" + 0
        );
      } else if (itemList.value[index].tracked_by == "Serial") {
        activeTab.value = "serial";
        input = document.getElementById("serial_no_" + item.items_id + "_" + 0);
      } else if (props.shelfManaged) {
        activeTab.value = "shelf";
        input = document.getElementById(
          "shelf_quantity_" + item.items_id + "_" + 0
        );
      } else {
        addItemRow(index);
      }
    } else {
      addItemRow(index);
    }
    nextTick(() => {
      if (input) {
        input.focus();
        (input as HTMLInputElement).select();
      }
    });
  });
}

const getShelfSum = (item: Item): number => {
  return (
    item.shelfs?.reduce((sum: number, shelf: WarehouseShelf) => {
      const quantity = Number(shelf.quantity);
      if (!isNaN(quantity)) {
        return sum + quantity;
      }
      return sum;
    }, 0) ?? 0
  );
};

const getBatchSum = (item: Item): number => {
  return (
    item.batches?.reduce((sum: number, batch: { quantity: number }) => {
      if (typeof batch.quantity === "number") {
        return sum + batch.quantity;
      }
      return sum;
    }, 0) ?? 0
  );
};

const getSerialSum = (item: Item): number => {
  return (
    item.serials?.reduce((sum: number, serial: ItemSerial) => {
      if (typeof serial.quantity === "number" && serial.serial_no) {
        return sum + serial.quantity;
      }
      return sum;
    }, 0) ?? 0
  );
};

const getVariantSum = (item: Item): number => {
  let variant = item.variant_list ? item.variant_list[0] : null;
  return (
    variant?.list?.reduce((sum: number, list: VariantList) => {
          const quantity = Number(list.quantity);
          if (!isNaN(quantity)) {
            return sum + quantity;
          }
          return sum;
        }, 0) ?? 0
  );
};

const removeItem = (index: number) => {
  itemList.value.splice(index, 1)
}

function handleShelfQuantityEnter(item: Item, batchIndex: number) {
  const qty = getShelfSum(item);
  if (qty == item.inventory_qty) {
    addItemRow(itemList.value.length - 1);
  } else {
    nextTick(() => {
      const input = document.getElementById(
        "shelf_quantity_" + item.items_id + "_" + (batchIndex + 1)
      );
      if (input) {
        input.focus();
        (input as HTMLInputElement).select();
      }
    });
  }
}

function handleBatchCodeEnter(item: Item, batchIndex: number) {
  const input = document.getElementById(
    "batch_expiry_" + item.items_id + "_" + batchIndex
  );
  if (input) {
    input.focus();
    (input as HTMLInputElement).select();
  }
}

function handleBatchExpiryEnter(item: Item, batchIndex: number) {
  nextTick(() => {
    const input = document.getElementById(
      "batch_quantity_" + item.items_id + "_" + batchIndex
    );
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
}

function handleSerialNoEnter(item: Item, serialIndex: number) {
  const input = document.getElementById(
    "serial_expiry_" + item.items_id + "_" + serialIndex
  );
  if (input) {
    input.focus();
    (input as HTMLInputElement).select();
  }
}

function handleSerialExpiryEnter(item: Item, serialIndex: number) {
  const qty = getSerialSum(item);
  if (qty == item.inventory_qty) {
    if (props.shelfManaged && getShelfSum(item) < item.inventory_qty) {
      activeTab.value = "shelfs";
      nextTick(() => {
        const input = document.getElementById(
          "shelf_quantity_" + item.items_id + "_0"
        );
        if (input) {
          input.focus();
          (input as HTMLInputElement).select();
        }
      });
    } else {
      addItemRow(itemList.value.length - 1);
    }
  } else {
    nextTick(() => {
      const input = document.getElementById(
        "serial_no_" + item.items_id + "_" + (serialIndex + 1)
      );
      if (input) {
        input.focus();
        (input as HTMLInputElement).select();
      }
    });
  }
}

function handleBatchQuantityEnter(item: Item, batchIndex: number) {
  const qty = getBatchSum(item);
  if (qty == item.inventory_qty) {
    if (props.shelfManaged && getShelfSum(item) < item.inventory_qty) {
      activeTab.value = "shelfs";
      nextTick(() => {
        const input = document.getElementById(
          "shelf_quantity_" + item.items_id + "_0"
        );
        if (input) {
          input.focus();
          (input as HTMLInputElement).select();
        }
      });
    } else {
      addItemRow(itemList.value.length - 1);
    }
  } else {
    addBatchRow(item);
    nextTick(() => {
      const input = document.getElementById(
        "batch_code_" + item.items_id + "_" + (batchIndex + 1)
      );
      if (input) {
        input.focus();
        (input as HTMLInputElement).select();
      }
    });
  }
}

const packingEditRowIndex = ref(-1);

const updatePackingPrice = (item: Item, packing: ItemPackingPrice) => {
  item.purchase_uom_id = packing.unit_of_measures_id;
  item.purchase_uom = packing.unit_of_measure;
  item.purchase_uom_qty = packing.inventory_qty;
  item.price = packing.selling_price;
  packingEditRowIndex.value = -1;
  updateLineTotal(item);
};

const clearPackingPrice = (item: Item) => {
  item.purchase_uom_id = item.inventory_uom_id;
  item.purchase_uom = item.unit_of_measure;
  item.purchase_uom_qty = item.inventory_qty;
  item.price = item.price;
  packingEditRowIndex.value = -1;
  updateLineTotal(item);
};

function updateDiscount(item: Item) {
  item.discount_p = parseFloat((((item.discount_a ?? 0) / (item.sub_total || 1)) * 100).toFixed(2));
  updateLineTotal(item);
}

// Add expanded items tracking
const expandedRows = ref<Set<string>>(new Set());

function toggleItemExpand(item: Item) {
  if (expandedRows.value.has(String(item.id))) {
    expandedRows.value.delete(String(item.id));
  } else {
    expandedRows.value.add(String(item.id));
  }
}

function isExpanded(item: Item): boolean {
  return expandedRows.value.has(String(item.id));
}

const closeItemSelect = () => {
  showItemsDialog.value = false;
  selectItems.value = [];
};

const showItemGridList = ref(false)
watch(
  () => props.items,
  () => {
    itemList.value = props.items ?? [];
    console.log('purchase grid items watch', itemList.value)
  },
  { immediate: true }
);

watch(
  () => props.allowNewRow,
  (newValue) => {
    if (newValue == true) {
      addItemRow(-1);
    }
  },
  { immediate: true }
);

const activeTab = ref("shelfs");

onMounted(() => {
  itemList.value = props.items ?? [];
  getTaxList();
});

const handleSelectedItems = async (items: Item[]) => {
  items.forEach(async item => {
    let hasItem = itemList.value.find((e) => e.code == item.code)
    if (hasItem) {
      selectedItem.value = item
      setItem(item, true)
    } else {
      let index = itemList.value.length - 1;
      if (index >= 0 && itemList.value[index].name == '') {
        addItemRow(index)
      }
      selectedItem.value = itemList.value[index]
      setItem(item, true)
      addItemRow(index + 1)
    }
  });
}

const insertItem = async (item: Item) => {
  let index = itemList.value.length - 1;
  await addItemRow(index)
  selectedItem.value = itemList.value[itemList.value.length -1]
  setItem(item, true)
}
defineExpose({ insertItem });

const mobileEditRowIndex = ref(-1)

function addItemFromMobile(item?: Item) {
  if (item) itemList.value.push(item);
  mobileEditRowIndex.value = -1
}

</script>

<template>
  <div class="mb-8">
    <div class="flex justify-between items-center mb-4" v-if="!readonly && allowNewRow">
      <div></div>
      <div>
      <button
        @click="showItemGridList = true"
        
        class="px-4 py-2 mr-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-blue-700"
      >
        Item Grid
      </button>
      <button
        @click="addItemRow(-1)"
        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
      >
        Add Item Row
      </button>
    </div>
    </div>

    <div
      class="relative overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg"
    >
      <div
        class="overflow-x-auto overflow-y-auto max-h-[500px] table-container"
      >
        <table v-if="!isMobile" class="w-full">
          <thead class="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <!-- Fixed left column -->
              <th
                class="z-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Code
              </th>

              <!-- Scrollable columns -->
              <!--<th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Photo</th>-->
              <th
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Name
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Variant
              </th>
              <!--<th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Item Type</th>-->
              <th
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Packing
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                On Hand
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Quantity
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Cost
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Price
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Sub Total
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Discount %
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Discount Amount
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Value Before Tax
              </th>
              <th
                class="px-4 min-w-[150px] py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Tax Basis
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Tax Amount
              </th>

              <!-- Fixed right column -->
              <th
                class="sticky right-0 bg-white dark:bg-gray-900 z-20 px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Net Total
              </th>
              <!--<th class="sticky right-0 z-20 px-4 py-3"></th>-->
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <template v-for="(item, index) in itemList" :key="item.id">
              <tr>
                <!-- Fixed left column -->
                <td class="z-10 px-4 py-3 w-[200px] min-w-60">
                  <div class="flex items-center">
                    <button
                      @click="toggleItemExpand(item)"
                      class="mr-2"
                      v-if="
                        (item.batches?.length ?? 0) > 0 ||
                        (item.shelfs?.length ?? 0) > 0 ||
                        (item.serials?.length ?? 0) > 0
                      "
                    >
                      <IconComponent
                        :name="isExpanded(item) ? 'ArrowUp' : 'ArrowDown'"
                      />
                    </button>
                    <div class="relative" v-if="!props.readonly">
                      <input
                        :id="'code_' + index"
                        v-model="item.code"
                        type="text"
                        class="w-full pl-3 pr-10 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
                        @keyup.enter="handleItemCodeEnter(item, index)"
                      />
                      <button
                        @click="showItemsDialog = true"
                        class="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Search items"
                      >
                        🔍
                      </button>
                    </div>
                    <span v-else>{{ item.code }}</span>
                  </div>
                </td>

                <!-- Scrollable columns -->
                <!--<td class="px-4 py-3">
                      <img 
                        v-if="item.photo" 
                        :src="item.photo" 
                        class="w-10 h-10 object-cover rounded"
                        alt=""
                      />
                    </td>-->
                <td class="px-4 py-3 min-w-60">{{ item.name }}</td>
                <td class="px-4 py-3">{{ item.variant_name }}</td>
                <td class="px-4 py-3 relative group">
                  <div
                    v-if="packingEditRowIndex == index"
                    class="right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20"
                  >
                    <button
                      v-for="p in item.packing_prices?.filter(
                        (e) => e.price_lists_id == props.price_lists_id
                      )"
                      @click="updatePackingPrice(item, p)"
                      :key="p.unit_of_measures_id"
                      class="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {{ p.unit_of_measure }} ({{ p.inventory_qty }}pcs) @
                      {{ formatNumber(p.selling_price) }}
                    </button>
                  </div>
                  <span
                    >{{ item.purchase_uom }} x
                    {{ formatNumber(item.purchase_uom_qty, 2, 0) }} pcs</span
                  >
                  <button
                    v-if="
                      packingEditRowIndex != index &&
                      !readonly &&
                      (item.packing_prices?.length ?? 0) > 0
                    "
                    @click="packingEditRowIndex = index"
                    class="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                    title="Edit item"
                  >
                  <IconComponent name="PackageSearch" />
                  </button>
                  <button
                    v-if="
                      packingEditRowIndex != index &&
                      !readonly &&
                      item.sales_uom_id != item.inventory_uom_id
                    "
                    @click="clearPackingPrice(item)"
                    class="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                    title="Clear"
                  >
                  <IconComponent name="Trash2" />
                  </button>
                  <button
                    v-if="packingEditRowIndex == index && !readonly"
                    @click="packingEditRowIndex = -1"
                    class="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                    title="Cancel item"
                  >
                  <IconComponent name="Ban" />
                  </button>
                </td>
                <td class="px-4 py-3 text-right">{{ item.on_hand }}</td>
                <td class="px-4 py-3 text-right relative">
                  <span
                  v-if="!readonly"
                    class="absolute top-1 right-2 text-[10px] text-gray-400"
                  >
                    {{ item.inventory_qty }}
                  </span>
                  <input
                    v-if="!props.readonly"
                    :id="'quantity_' + index"
                    v-model.number="item.quantity"
                    type="number"
                    min="1"
                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                    @input="updateLineTotal(item)"
                    @keyup.enter="handleQuantityEnter(index)"
                  />
                  <span v-else>{{ item.quantity }}</span>
                </td>
                <td class="px-4 py-3 text-right">
                  <input
                    v-if="!props.readonly"
                    :id="'cost_' + index"
                    v-model.number="item.cost"
                    type="number"
                    min="1"
                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                    @input="updateLineTotal(item)"
                    @keyup.enter="handleCostEnter(index)"
                  />
                  <span v-else>{{ formatNumber(item.cost / (item.exchange_rate ?? exchange_rate ?? 1)) }}</span>
                </td>
                <td class="px-4 py-3 text-right">
                  <input
                    v-if="!props.readonly"
                    :id="'price_' + index"
                    v-model.number="item.price"
                    type="number"
                    min="1"
                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                    @keyup.enter="handleUnitPriceEnter(item, index)"
                  />
                  <span v-else>{{ item.price }}</span>
                </td>
                <td class="px-4 py-3 text-right">
                  {{ formatNumber(item.sub_total / (item.exchange_rate ?? exchange_rate ?? 1)) }}
                </td>
                <td class="px-4 py-3 text-right">
                  <input
                    v-if="!props.readonly"
                    v-model.number="item.discount_p"
                    type="number"
                    min="0"
                    max="100"
                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                    @input="updateLineTotal(item)"
                  />
                  <span v-else>{{ item.discount_p }}</span>
                </td>
                <td class="px-4 py-3 text-right">
                  {{ formatNumber((item.discount_a ?? 0) / (item.exchange_rate ?? exchange_rate ?? 1)) }}
                </td>
                <td class="px-4 py-3 text-right"> 
                  {{ formatNumber((item.value_before_tax ?? 0)  / (item.exchange_rate ?? exchange_rate ?? 1)) }}
                </td>
                <td class="px-4 py-3 text-left">
                  <SearchSelect
                    label=""
                    v-if="!readonly"
                    :options="taxDetails"
                    v-model="item.tax_details_id"
                    @select="handleTax(item)"
                  />
                  <span  v-else>{{ item.tax }}</span>
                </td>
                <td class="px-4 py-3 text-right relative">
                  <span
                    class="absolute top-1 right-2 text-[10px] text-gray-400"
                  >
                    {{ item.tax_rate }}%
                  </span>
                  {{ formatNumber((item.tax_amount ?? 0) / (item.exchange_rate ?? exchange_rate ?? 1)) }}
                </td>

                <!-- Fixed right columns -->
                <td
                  class="sticky right-0 min-w-30 bg-white dark:bg-gray-900 z-10 px-4 py-3 text-right font-medium"
                >
                  {{ formatNumber((item.net_total ?? 0) / (item.exchange_rate ?? exchange_rate ?? 1)) }}
                  <button
                    v-if="!readonly"
                        @click="removeItem(index)"
                        class="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <IconComponent name="Trash2" />
                      </button>
                </td>
              </tr>
              <tr v-if="isExpanded(item) && item.name && item.code" :key="'expand-' + item.id">
                <td colspan="13" class="py-2 px-4 bg-white dark:bg-gray-900">
                  <TabComponent
                    v-model="activeTab"
                    :tabs="[
                      {
                        name: 'shelfs',
                        label: 'Shelfs',
                        visible: props.shelfManaged && item.tracked_by != 'Batch' && item.tracked_by != 'Serial',
                      },
                      {
                        name: 'batch',
                        label: 'Batches',
                        visible: item.tracked_by == 'Batch',
                      },
                      {
                        name: 'serial',
                        label: 'Serials',
                        visible: item.tracked_by == 'Serial',
                      },
                      /*{
                        name: 'variants',
                        label: 'Variants',
                        visible: (item.variant_list?.length ?? 0) > 0,
                      },*/
                    ]"
                  >
                    <template #batch>
                      <div
                      v-if="!readonly"
                        class="md:flex justify-between items-center mb-6 w-[500px]"
                      >
                        <Buttons
                          @click="() => addBatchRow(item)"
                          v-if="!isReadonly"
                          caption="Add new Batch"
                        ></Buttons>
                        <span
                          class="text-2xl font-medium text-gray-900 dark:text-white"                          
                          :class="{
                            'bg-red-500 rounded-md p-2':
                              getBatchSum(item) != item.inventory_qty,
                          }"
                          >Total: {{ getBatchSum(item) }}, should be:
                          {{ item.inventory_qty }}</span
                        >
                      </div>
                      <div>
                        <table class="w-[500px]">
                          <thead class="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                            <tr>
                              <th
                                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Batch No
                              </th>
                              <th
                                v-if="item.is_expiry == true"
                                class="px-4 w-[100px] py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Expiry Date
                              </th>
                              <th
                                v-if="props.shelfManaged"
                                class="px-4 w-[100px] py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Shelf
                              </th>
                              <th
                                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                On Hand
                              </th>
                              <th
                                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Quantity
                              </th>
                              <th class="sticky right-0 z-20 px-4 py-3"></th>
                            </tr>
                          </thead>
                          <tbody
                            class="divide-y divide-gray-200 dark:divide-gray-700"
                          >
                            <template
                              v-for="(batch, batchIndex) in item.batches"
                              :key="'batch_' + item.id"
                            >
                              <tr>
                                <td class="px-4 py-3 min-w-[250px]">
                                  <TextInput
                                    v-if="!readonly"
                                    v-model="batch.batch_no"
                                    label=""
                                    :id="
                                      'batch_code_' +
                                      item.items_id +
                                      '_' +
                                      batchIndex
                                    "
                                    @keyup.enter="
                                      handleBatchCodeEnter(item, batchIndex)
                                    "
                                  />
                                  <span v-else>{{ batch.batch_no }}</span>
                                </td>
                                <td
                                  class="px-4 py-3 min-w-[150px]"
                                  v-if="item.is_expiry == true"
                                >
                                  <span v-if="readonly">{{
                                    formatDate(batch.expiry_date)
                                  }}</span>
                                  <DateInput
                                    v-else
                                    v-model="batch.expiry_date"
                                    label=""
                                    :id="
                                      'batch_expiry_' +
                                      item.items_id +
                                      '_' +
                                      batchIndex
                                    "
                                    @keyup.enter="
                                      handleBatchExpiryEnter(item, batchIndex)
                                    "
                                  />
                                </td>
                                <td
                                  class="px-4 py-3 min-w-[150px]"
                                  v-if="props.shelfManaged"
                                >
                                <SelectDropDown
                                label=""
                                  v-if="!readonly"
                                  :options="shelfs.map(({ id, name }) => ({ id, name: name ?? '' }))"
                                  v-model="batch.warehouse_shelfs_id"
                                />
                              </td>
                                <td class="px-4 py-3 text-right">
                                  {{ batch.on_hand }}
                                </td>
                                <td class="px-4 py-3 text-right">
                                  <input
                                    @keyup.enter="
                                      handleBatchQuantityEnter(item, batchIndex)
                                    "
                                    v-if="!props.readonly"
                                    :id="
                                      'batch_quantity_' +
                                      item.items_id +
                                      '_' +
                                      batchIndex
                                    "
                                    v-model.number="batch.quantity"
                                    type="number"
                                    min="1"
                                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                                  />
                                  <span v-else>{{ batch.quantity }}</span>
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </table>
                      </div>
                    </template>
                    <template #serial>
                      <div
                      v-if="!readonly"
                        class="md:flex justify-between items-center mb-6 w-[500px]"
                      >
                        <span
                          class="text-2xl font-medium text-gray-900 dark:text-white"
                          >Serials</span
                        >
                        <Buttons
                          @click="() => addSerialRow(item)"
                          v-if="!isReadonly && (item.serials?.length ?? 0) < (item.inventory_qty ?? 0)"
                          caption="Add new Serial"
                        ></Buttons>
                        <span
                          class="text-2xl font-medium text-gray-900 dark:text-white"                          
                          :class="{
                            'bg-red-500 rounded-md p-2':
                              getSerialSum(item) != item.inventory_qty,
                          }"
                          >Total: {{ getSerialSum(item) }}, should be:
                          {{ item.inventory_qty }}</span
                        >
                      </div>
                      <div>
                        <table class="w-[500px]">
                          <thead class="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                            <tr>
                              <th
                                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Serial No
                              </th>
                              <th
                                v-if="props.shelfManaged"
                                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Shelf
                              </th>
                              <th
                                v-if="item.is_expiry == true"
                                class="px-4 w-[100px] py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Expiry Date
                              </th>
                              <th class="sticky right-0 z-20 px-4 py-3"></th>
                            </tr>
                          </thead>
                          <tbody
                            class="divide-y divide-gray-200 dark:divide-gray-700"
                          >
                            <template
                              v-for="(serial, serialIndex) in item.serials"
                              :key="'serial_' + item.id"
                            >
                              <tr>
                                <td class="px-4 py-3 min-w-[200px]">
                                  <TextInput
                                    v-if="!readonly"
                                    v-model="serial.serial_no"
                                    label=""
                                    :id="
                                      'serial_no_' +
                                      item.items_id +
                                      '_' +
                                      serialIndex
                                    "
                                    @keyup.enter="
                                      handleSerialNoEnter(item, serialIndex)
                                    "
                                  />
                                  <span v-else>{{ serial.serial_no }}</span>
                                </td>
                                <td
                                  class="px-4 py-3 min-w-[150px]"
                                  v-if="props.shelfManaged"
                                >
                                <SearchSelect
                                  label=""
                                  v-if="!readonly"
                                  :options="shelfs.map(({ id, name }) => ({ id, name: name ?? '' }))"
                                  v-model="serial.warehouse_shelfs_id"
                                  @select="handleTax(item)"
                                />cls
                                {{ serial.shelf ?? serial.shelf }}
                              </td>
                                <td
                                  class="px-4 py-3 w-[150px]"
                                  v-if="item.is_expiry == true"
                                >
                                  <span v-if="readonly">{{
                                    formatDate(
                                      serial.expiry_date as string | Date
                                    )
                                  }}</span>
                                  <DateInput
                                    v-else
                                    v-model="serial.expiry_date as string | Date"
                                    label=""
                                    :id="
                                      'serial_expiry_' +
                                      item.items_id +
                                      '_' +
                                      serialIndex
                                    "
                                    @keyup.enter="
                                      handleSerialExpiryEnter(item, serialIndex)
                                    "
                                  />
                                </td>
                                <td class="px-4 py-3 text-right">
                                  <span>{{ serial.quantity }}</span>
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </table>
                      </div>
                    </template>
                    <template #shelfs>
                      <div
                        class="md:flex justify-between items-center mb-6 w-[500px]"
                      >
                        <span
                          class="text-2xl font-medium text-gray-900 dark:text-white"
                          :class="{
                            'bg-red-500 rounded-md p-2':
                              getShelfSum(item) != item.inventory_qty,
                          }"
                          >Total: {{ getShelfSum(item) }}, should be:
                          {{ item.inventory_qty }}</span
                        >
                      </div>
                      <div>
                        <table class="w-[500px]">
                          <thead class="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                            <tr>
                              <th
                                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Shelf Code
                              </th>
                              <!--<th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Item Type</th>-->
                              <th
                                class="px-4 w-[100px] py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Shelf Name
                              </th>
                              <th
                                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                Quantity
                              </th>
                              <th class="sticky right-0 z-20 px-4 py-3"></th>
                            </tr>
                          </thead>
                          <tbody
                            class="divide-y divide-gray-200 dark:divide-gray-700"
                          >
                            <template
                              v-for="(shelf, shelfIndex) in item.shelfs"
                              :key="'batch_' + item.id"
                            >
                              <tr>
                                <td class="px-4 py-3">
                                  {{ shelf.code ?? shelf.warehouse_shelf_code }}
                                </td>
                                <td class="px-4 py-3 w-[150px]">
                                  {{ shelf.name ?? shelf.warehouse_shelf_name }}
                                </td>
                                <td class="px-4 py-3 text-right">
                                  <input
                                    @keyup.enter="
                                      handleShelfQuantityEnter(item, shelfIndex)
                                    "
                                    v-if="!props.readonly"
                                    :id="
                                      'shelf_quantity_' +
                                      item.items_id +
                                      '_' +
                                      shelfIndex
                                    "
                                    v-model.number="shelf.quantity"
                                    type="number"
                                    min="1"
                                    class="w-24 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                                  />
                                  <span v-else>{{ shelf.quantity }}</span>
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </table>
                      </div>
                    </template>
                    <template #variants>
                      <div
                        class="md:flex justify-between items-center mb-6 w-[500px]"
                      >
                        <span
                          class="text-2xl font-medium text-gray-900 dark:text-white"
                          :class="{
                            'bg-red-500 rounded-md p-2':
                              getVariantSum(item) != item.inventory_qty,
                          }"
                          >Total: {{ getVariantSum(item) }}, should be:
                          {{ item.inventory_qty }}</span
                        >
                      </div>
                    </template>
                  </TabComponent>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <template v-else>
          <ItemSearchMobile
            :allow-change-price="allowChangePrice"
            @showItemGrid="showItemGridList = true"
            :allow-discount="allowDiscount"
            :allow-new-row="allowNewRow"
            :currency="currency"
            :exchange_rate="exchange_rate"
            :ignore-batch-serial="ignoreBatchSerial"
            :price_lists_id="price_lists_id"
            :readonly="readonly"
            :shelf-managed="shelfManaged"
            :shelfs="shelfs"
            :warehouses_id="warehouses_id"
            @item="setItem"
            :tax-details="taxDetails"
            :vendor="vendor"
            @add-item="addItemFromMobile"
          />
          <div class="md:hidden space-y-4" id="table-records">
            <template v-for="(item, index) in itemList"
          :key="item.id">
        <div
          v-if="!isEmpty(item.name)"
          class="bg-white dark:bg-gray-900 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700"
        >
          <div class="space-y-2 text-sm">
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Code</span>
              <span class="font-medium">{{ item.code }}</span>
            </div>
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Name</span>
              <span class="font-medium">{{ item.name || item.item}}</span>
            </div>
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Quantity1</span>
              <input
                v-if="!props.readonly && mobileEditRowIndex == index"
                :id="'quantity_mobile_' + index"
                v-model.number="item.quantity"
                type="number"
                min="1"
                class="px-3 py-2 max-w-[120-px] w-[120px] rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                @input="updateLineTotal(item)"
                @keyup.enter="handleQuantityEnter(index, true)"
              />
              <span v-else class="font-medium">{{ item.quantity }}</span>
            </div>
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Cost</span>
              <input
                v-if="!props.readonly && mobileEditRowIndex == index"
                :id="'cost_mobile_' + index"
                v-model.number="item.cost"
                type="number"
                :min="0"
                class="px-3 py-2 max-w-[120-px] w-[120px] rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                @input="updateLineTotal(item)"
                @keyup.enter="handleCostEnter(index, true)"
              />
              <span v-else class="font-medium">{{ item.quantity }}</span>
            </div>
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Selling Price</span>
              <input
                v-if="!props.readonly && mobileEditRowIndex == index"
                :id="'price_mobile_' + index"
                v-model.number="item.price"
                type="number"
                :min="0"
                class="px-3 py-2 max-w-[120-px] w-[120px] rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                @input="updateLineTotal(item)"
                @keyup.enter="handleUnitPriceEnter(item, index, true)"
              />
              <span v-else class="font-medium">{{ item.quantity }}</span>
            </div>
            <div key="mobile-code" class="flex justify-between" v-if="!props.readonly && mobileEditRowIndex == index">
              <span class="text-gray-500 dark:text-gray-400">Discount %</span>
              <input
                v-model.number="item.discount_p"
                :id="'discount_p_mobile_' + index"
                type="number"
                min="0"
                max="100"
                class="px-3 py-2 max-w[120px] min-w-[120px] rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                @keyup.enter="updateDiscount(item)"
              />
            </div>
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Discount</span>
              <input
                v-if="!props.readonly && mobileEditRowIndex == index"
                :id="'discount_a_mobile_' + index"
                v-model.number="item.discount_a"
                type="number"
                min="0"
                max="100"
                class="px-3 py-2 max-w[120px] min-w-[120px] rounded-lg border border-gray-200 dark:border-gray-700 text-right"
                @keyup.enter="updateDiscount(item)"
              />
              <span class="font-medium" v-else>{{ formatNumber((item.discount_a ?? 0) / (item.exchange_rate ?? exchange_rate ?? 1)) }}</span>
            </div>
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Line Total</span>
              <span class="font-medium">{{ formatNumber((item.value_before_tax) / (item.exchange_rate ?? exchange_rate ?? 1)) }}</span>
            </div>
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Tax Amount ({{ item.tax }})</span>
              <span class="font-medium">{{ formatNumber((item.tax_amount) / (item.exchange_rate ?? exchange_rate ?? 1)) }}</span>
            </div>
            <div key="mobile-code" class="flex justify-between">
              <span class="text-gray-500 dark:text-gray-400">Total</span>
              <span class="font-medium">{{ formatNumber((item.net_total) / (item.exchange_rate ?? exchange_rate ?? 1)) }}</span>
            </div>
          </div>
          <div
            class="mt-4 flex justify-end space-x-2 border-t dark:border-gray-700 pt-4"
            id="sales-documents-actions"
            v-if="!readonly"
          >
          <button
              :key="'edit-item=' + item.id"
              v-if="mobileEditRowIndex != index"
              @click="mobileEditRowIndex = index"
              class="[ 'p-2', 'text-warning-600 hover:text-warning-700', 'dark:text-warning-400 dark:hover:text-warning-300' ]"
              title="Edit Item"
            >
              <IconComponent name="Pencil" />
            </button>
            <button
              :key="'edit-item=' + item.id"
              v-if="mobileEditRowIndex == index"
              @click="mobileEditRowIndex = -1"
              class="[ 'p-2', 'text-warning-600 hover:text-warning-700', 'dark:text-warning-400 dark:hover:text-warning-300' ]"
              title="Edit Item"
            >
              <IconComponent name="Save" />
            </button>
            <button
              :key="'remove-item=' + item.id"
              @click="itemList.splice(index, 1)"
              class="[ 'p-2', 'text-warning-600 hover:text-warning-700', 'dark:text-warning-400 dark:hover:text-warning-300' ]"
              title="Remove Item"
            >
              <IconComponent name="Trash2" />
            </button>
          </div>
        </div><!-- end of the product border-->
      </template> 
      </div>
      </template>
      </div>
    </div>

    <!-- Totals -->
    <div class="mt-4 flex justify-end">
      <div class="w-72 space-y-2">
        <div class="flex justify-between text-sm">
          <span class="text-gray-600 dark:text-gray-400">Subtotal</span>
          <span class="font-medium">{{ formatNumber(subtotal / (exchange_rate ?? 1)) }}</span>
        </div>
        <div class="flex justify-between text-sm">
          <span class="text-gray-600 dark:text-gray-400">Tax</span>
          <span class="font-medium">{{ formatNumber(taxAmount / (exchange_rate ?? 1)) }}</span>
        </div>
        <div
          class="flex justify-between text-lg font-medium pt-2 border-t border-gray-200 dark:border-gray-700"
        >
          <span>Total</span>
          <span>{{ formatNumber(total / (exchange_rate ?? 1)) }}</span>
        </div>
      </div>
    </div>

    <ItemGridSelectItems
      v-if="showItemsDialog"
      :items="selectItems"
      :warehouses_id="warehouses_id"
      :price_lists_id="price_lists_id"
      @close="closeItemSelect"
      @select-many="handleSelectedItems"
      @select="setItem"
    />
    <ItemSelectGrid
      v-if="showItemGridList"
      :warehouses_id="props.warehouses_id"
      :price_lists_id="props.price_lists_id"
      @close="showItemGridList = false"
      @selected="handleSelectedItems"
    />

    <SelectVariants 
    v-if="showVariantsDialog && selectedItem"
    @close="showVariantsDialog = false"
    @select="handleSelectVariant"
    :item="selectedItem"
    />

    <ItemsForm
        v-if="showItemForm"
        :list-create-code="selectedItem?.code"
        :ignore-permission="true"
        end-point="/clients/inventory/item-management/items/"
        title="Create Item"
        mode="create"
        @save="handleItemCreated"
        @close="showItemForm = false"
      />


  </div>
</template>
<style scoped>
.table-container {
  max-height: 800px; /* Adjust height as needed */
  overflow-y: auto;
}
</style>
